import React from "react";
import Seo from "../components/Seo.jsx";

/* Onboarding shell — v2.
   welcome → describe (paste URL or one-liner) → analyzing → radar.

   This file owns the entire post-welcome onboarding flow: one input upfront,
   AI fills in everything, user reviews and tweaks the radar inline.

   Step names are kept compatible with the old shell so app.jsx routing
   doesn't need to change: "setup" → DescribeProductScreen, "map" →
   RadarReviewScreen. */

const OB_EMPTY_FORM = {
  name: "",
  website: "",
  description: "",
  customers: "",
  useCases: [],
  painPoints: [],
  competitors: [],
  keywords: [],
  avoid: [],
  subreddits: [],
};

const OB_EMPTY_PLAN = {
  target_customers: [],
  jobs_to_be_done: [],
  pain_points: [],
  trigger_phrases: [],
  suggested_subreddits: [],
  negative_matches: [],
  search_themes: [],
};

// Accepts a bare name ("AskReddit"), a path ("/r/AskReddit"), or a full Reddit
// URL ("https://www.reddit.com/r/AskReddit/"). Returns just the subreddit
// name with original casing — backend dedups and ingests case-insensitively.
function parseSubredditName(raw) {
  const s = String(raw || "").trim();
  if (!s) return "";
  const m = s.match(/(?:^|[\/.])r\/([A-Za-z0-9_]{2,21})/i);
  if (m) return m[1];
  return s.replace(/^\/+/, "").split(/[\/?#\s]/)[0];
}

function formatSubscribers(n) {
  const num = Number(n);
  if (!Number.isFinite(num) || num <= 0) return "";
  if (num >= 1_000_000) return (num / 1_000_000).toFixed(num >= 10_000_000 ? 0 : 1) + "M";
  if (num >= 1_000) return (num / 1_000).toFixed(num >= 10_000 ? 0 : 1) + "k";
  return String(num);
}

function inferSignal(subscribers) {
  const n = Number(subscribers);
  if (!Number.isFinite(n) || n <= 0) return "";
  if (n >= 500_000) return "high";
  if (n >= 50_000) return "medium";
  return "low";
}

function SubredditAvatar({ name, icon }) {
  const label = String(name || "").trim().replace(/^r\//i, "");
  if (icon) {
    return (
      <span className="ob2-sub-avatar" aria-hidden="true">
        <img className="ob2-sub-avatar-img" src={icon} alt="" loading="lazy" />
      </span>
    );
  }
  return (
    <span className="ob2-sub-avatar ob2-sub-avatar-fallback" aria-hidden="true">
      {label ? label.slice(0, 1).toUpperCase() : "r"}
    </span>
  );
}

// Treat input as a URL/domain when it has a scheme or a bare "name.tld" shape.
function looksLikeUrl(value) {
  const s = String(value || "").trim();
  if (!s || /\s/.test(s)) return /^https?:\/\//i.test(s);
  return /^https?:\/\//i.test(s) || /^[\w-]+(\.[\w-]+)+(\/\S*)?$/.test(s);
}

function OnboardingShell({
  step,
  onStepChange,
  onApprove,
  onSkipDemo,
  onLogin,
  onExitHome,
  seed,
  onSeedChange,
  onClearSignupIntent,
  searchPlan,
  onPlanGenerated,
  theme,
  onToggleTheme,
}) {
  // step: "welcome" | "setup" | "map"

  // Inferred form (product_name, customers, …) — produced by the analyze step
  // and edited inside the radar screen. We hold it here so going back from
  // radar → describe doesn't drop the user's edits.
  const [inferredForm, setInferredForm] = React.useState(
    () => window.api?.storage?.readPendingProfile?.() || null
  );

  const clearSeed = () => { if (onSeedChange) onSeedChange(null); };
  const dropSignupIntent = () => { if (onClearSignupIntent) onClearSignupIntent(); };

  const goRadar = (form, plan) => {
    setInferredForm(form);
    try { window.api?.storage?.writePendingProfile?.(form); } catch (e) {}
    if (plan && onPlanGenerated) onPlanGenerated(plan);
    clearSeed();
    onStepChange("map");
  };

  const restartDescribe = () => {
    setInferredForm(null);
    try { window.api?.storage?.clearPendingProfile?.(); } catch (e) {}
    try { window.api?.storage?.clearPendingPlan?.(); } catch (e) {}
    if (onPlanGenerated) onPlanGenerated(null);
    clearSeed();
    dropSignupIntent();
    onStepChange("setup");
  };

  const resetToWelcome = () => {
    setInferredForm(null);
    clearSeed();
    dropSignupIntent();
    try { window.api?.storage?.clearPendingProfile?.(); } catch (e) {}
    try { window.api?.storage?.clearPendingPlan?.(); } catch (e) {}
    if (onPlanGenerated) onPlanGenerated(null);
    if (onExitHome) {
      onExitHome();
      return;
    }
    onStepChange("welcome");
  };

  const onRadarApprove = (form, plan) => {
    setInferredForm(form);
    try { window.api?.storage?.writePendingProfile?.(form); } catch (e) {}
    if (plan && onPlanGenerated) onPlanGenerated(plan);
    onApprove?.();
  };

  return (
    <div className={"onboard-wrap ob-step-" + step}>
      <Seo title="Get started" description="Set up Community Ninja for your business." noindex />
      {step !== "welcome" && (
        <div className="onboard-topbar">
          <button
            className="sidebar-logo"
            style={{ border: "none", padding: 0, margin: 0, background: "none" }}
            onClick={resetToWelcome}
            type="button"
          >
            <img
              src="/assets/icon.png"
              alt="Community Ninja"
              className="logo-mark"
              style={{ background: "none" }}
            />
            <div className="logo-name">Community Ninja</div>
          </button>
          <div className="onboard-stepper">
            <div className={"step-dot " + (step === "setup" ? "is-current" : "is-done")}>1</div>
            <div className="step-line" />
            <div className={"step-dot " + (step === "map" ? "is-current" : "")}>2</div>
            <span style={{ marginLeft: 6 }}>
              {step === "setup" && "Describe"}
              {step === "map" && "Review radar"}
            </span>
          </div>
          <div className="spacer" />
          {onToggleTheme && (
            <button
              className="theme-toggle"
              onClick={onToggleTheme}
              aria-label={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"}
              title={theme === "dark" ? "Light mode" : "Dark mode"}
            >
              {theme === "dark"
                ? <window.I.Sun size={15} />
                : <window.I.Moon size={15} />}
            </button>
          )}
        </div>
      )}

      <div className={"onboard-body " + (step === "welcome" ? "is-welcome" : "")}>
        {step === "welcome" && (
          <window.WelcomeScreen
            onStart={() => onStepChange("setup")}
            onBuildRadar={(value) => {
              if (onSeedChange) onSeedChange(value ? value.trim() : null);
              onStepChange("setup");
            }}
            onExample={onSkipDemo}
            onLogin={onLogin}
            theme={theme}
            onToggleTheme={onToggleTheme}
          />
        )}
        {step === "setup" && (
          <DescribeProductScreen
            seed={seed}
            onAnalyzed={goRadar}
            onBack={() => { clearSeed(); onStepChange("welcome"); }}
          />
        )}
        {step === "map" && (
          <RadarReviewScreen
            form={inferredForm || OB_EMPTY_FORM}
            searchPlan={searchPlan || OB_EMPTY_PLAN}
            onApprove={onRadarApprove}
            onRestart={restartDescribe}
          />
        )}
      </div>
    </div>
  );
}


/* ─────────── Step 1 · Describe ─────────── */

function DescribeProductScreen({ onAnalyzed, onBack, seed }) {
  const seedIsUrl = seed ? looksLikeUrl(seed) : false;
  const [mode, setMode] = React.useState(seedIsUrl || !seed ? "website" : "description");
  const [website, setWebsite] = React.useState(
    seedIsUrl ? String(seed).trim().replace(/^https?:\/\//i, "") : ""
  );
  const [description, setDescription] = React.useState(
    seed && !seedIsUrl ? String(seed).trim() : ""
  );
  // Start in the analyzing state when seeded so there's no flash of the form.
  const [analyzing, setAnalyzing] = React.useState(!!seed);
  const [error, setError] = React.useState("");

  const canAnalyze =
    mode === "website" ? website.trim().length > 3 : description.trim().length > 10;

  const runInfer = async () => {
    setAnalyzing(true);
    setError("");
    try {
      const resp = await window.api.profile.infer({ mode, website, description });
      const form = { ...OB_EMPTY_FORM, ...(resp?.form || {}) };
      // Make sure list-shaped fields are arrays (defensive — server should
      // already return arrays).
      for (const k of ["useCases", "painPoints", "competitors", "keywords", "avoid", "subreddits"]) {
        if (!Array.isArray(form[k])) form[k] = [];
      }
      onAnalyzed(form, resp?.search_plan || null);
    } catch (e) {
      setError(e?.message || "Could not analyze. Please try again.");
      setAnalyzing(false);
    }
  };

  const submit = () => {
    if (!canAnalyze || analyzing) return;
    runInfer();
  };

  // Seeded from the welcome prompt → skip the describe screen entirely and go
  // straight to building the radar. On failure we fall back to the form below
  // (prefilled with the seed) so the user can retry.
  React.useEffect(() => {
    if (seed) runInfer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (analyzing) return <AnalyzingTransition mode={mode} />;

  return (
    <div className="ob2-describe screen-enter">
      <div className="ob2-eyebrow">
        <span className="ob2-eyebrow-dot" /> Step 1 of 2 · About 30 seconds
      </div>
      <h1 className="ob2-title">What are you working on?</h1>
      <p className="ob2-sub">
      Drop a link or describe your product, market, or topic in one sentence. We'll infer the audience, pains, questions, and communities worth watching. You can edit everything later.
      </p>

      <div className="ob2-card">
        <div className="ob2-segmented" role="tablist">
          <button
            role="tab"
            aria-selected={mode === "website"}
            className={"ob2-seg " + (mode === "website" ? "is-on" : "")}
            onClick={() => setMode("website")}
          >
            <window.I.External size={13} />
            Paste a link
          </button>
          <button
            role="tab"
            aria-selected={mode === "description"}
            className={"ob2-seg " + (mode === "description" ? "is-on" : "")}
            onClick={() => setMode("description")}
          >
            <window.I.Edit size={13} />
            Describe it
          </button>
          <span className={"ob2-seg-thumb " + (mode === "description" ? "is-right" : "")} />
        </div>

        <div className="ob2-input-area">
          {mode === "website" ? (
            <>
              <div className="ob2-url">
                <span className="ob2-url-prefix">https://</span>
                <input
                  className="ob2-url-input"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && submit()}
                  placeholder="yourwebsite.com"
                />
              </div>
              <div className="ob2-input-hint">
                <window.I.Sparkles size={11} />
                We'll read your website to extract the basics.
              </div>
            </>
          ) : (
            <>
              <textarea
                className="ob2-textarea"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="A tool, market, or topic you want to monitor…"
                rows={4}
              />
              <div className="ob2-input-hint">
                <window.I.Sparkles size={11} />
                Don't overthink it.
              </div>
            </>
          )}
        </div>

        {error && (
          <div className="ob2-input-error">
            <window.I.Alert size={12} />
            {error}
          </div>
        )}

        <div className="ob2-card-foot">
          {onBack ? (
            <button className="ob2-link" onClick={onBack}>
              <window.I.ChevronLeft size={12} />
              Back
            </button>
          ) : (
            <span />
          )}
          <div className="spacer" />
          <button
            className="btn btn-primary btn-lg"
            onClick={submit}
            disabled={!canAnalyze}
          >
            <window.I.Sparkles size={14} />
            Build my radar
          </button>
        </div>
      </div>
    </div>
  );
}


/* ─────────── Transition · Analyzing ─────────── */

function AnalyzingTransition({ mode = "website" }) {
  const steps = mode === "website" ? [
    { label: "Reading your site",          icon: "External" },
    { label: "Understanding your customer", icon: "User" },
    { label: "Surfacing pain signals",      icon: "Sparkles" },
    { label: "Mapping subreddits",          icon: "Layers" },
    { label: "Almost done",                 icon: "Loader" },
  ] : [
    { label: "Reading your pitch",          icon: "Edit" },
    { label: "Understanding your customer", icon: "User" },
    { label: "Surfacing pain signals",      icon: "Sparkles" },
    { label: "Mapping subreddits",          icon: "Layers" },
    { label: "Almost done",                 icon: "Loader" },
  ];
  const [phase, setPhase] = React.useState(0);

  React.useEffect(() => {
    const id = setInterval(
      () => setPhase((p) => Math.min(p + 1, steps.length - 1)),
      3000
    );
    return () => clearInterval(id);
  }, [steps.length]);

  return (
    <div className="ob2-analyzing screen-enter">
      <div className="ob2-analyze-orb">
        <span className="ob2-orb-pulse" />
        <span className="ob2-orb-pulse ob2-orb-pulse--2" />
        <window.I.Sparkles size={26} style={{ animation: "spin 900ms linear infinite" }} />
      </div>
      <div className="ob2-analyze-title">Building your radar</div>
      <div className="ob2-analyze-sub">This takes a few seconds.</div>

      <div className="ob2-analyze-steps">
        {steps.map((s, i) => {
          const state =
            i < phase ? "done" : i === phase ? "active" : "pending";
          return (
            <div key={i} className={"ob2-analyze-step is-" + state}>
              <span className="ob2-step-dot">
                {state === "done" ? (
                  <window.I.Check size={12} />
                ) : state === "active" ? (
                  <window.I.Sparkles size={12} style={{ animation: "spin 900ms linear infinite" }} />
                ) : (
                  <span className="ob2-step-ring" />
                )}
              </span>
              <span className="ob2-step-label">{s.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}


/* ─────────── Step 2 · Radar review ─────────── */

function RadarReviewScreen({ form, searchPlan, onApprove, onRestart, submitting = false }) {
  const plan = searchPlan || OB_EMPTY_PLAN;

  // Product card — sourced from inferred form, editable inline.
  const [productName, setProductName] = React.useState(form?.name || "");
  const [pitch, setPitch] = React.useState(form?.description || "");
  const [customer, setCustomer] = React.useState(form?.customers || "");
  const [website, setWebsite] = React.useState(form?.website || "");
  const [siteIconFailed, setSiteIconFailed] = React.useState(false);
  const [editingPitch, setEditingPitch] = React.useState(false);
  const [editingCustomer, setEditingCustomer] = React.useState(false);

  // Subreddits — merge form.subreddits and plan.suggested_subreddits. Plan
  // wins for size/reason/signal; form-only names fall through as "medium".
  const initialSubs = React.useMemo(
    () => mergeSubreddits(form?.subreddits || [], plan.suggested_subreddits || []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const [subs, setSubs] = React.useState(initialSubs);
  const [addingSub, setAddingSub] = React.useState(false);
  const [subredditIcons, setSubredditIcons] = React.useState({});

  // Match signals — seeded from search_plan, editable as chips.
  const [jobs, setJobs] = React.useState(plan.jobs_to_be_done || []);
  const [pains, setPains] = React.useState(plan.pain_points || form?.painPoints || []);
  const [keywords, setKeywords] = React.useState(form?.keywords || []);
  const [triggers, setTriggers] = React.useState(plan.trigger_phrases || []);
  const [themes, setThemes] = React.useState(plan.search_themes || []);
  const [avoid, setAvoid] = React.useState(form?.avoid || []);
  const [negatives, setNegatives] = React.useState(plan.negative_matches || []);

  // Collapsible themes section.
  const [advancedOpen, setAdvancedOpen] = React.useState(false);

  const sourceUrl = (form?.sourceUrl || "").trim();
  const sourceIcon = (form?.sourceIcon || "").trim();
  const fallbackIcon = React.useMemo(() => {
    const raw = sourceUrl || website;
    if (!raw) return "";
    try {
      const normalized = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
      const parsed = new URL(normalized);
      return `${parsed.protocol}//${parsed.host}/favicon.ico`;
    } catch (e) {
      return "";
    }
  }, [sourceUrl, website]);
  const siteIcon = !siteIconFailed ? (sourceIcon || fallbackIcon) : "";

  const toggleSub = (name) =>
    setSubs((arr) => arr.map((s) => (s.name === name ? { ...s, on: !s.on } : s)));
  const removeSub = (name) =>
    setSubs((arr) => arr.filter((s) => s.name !== name));
  const addSub = (raw) => {
    const cleaned = parseSubredditName(raw);
    if (!cleaned) return;
    const name = "r/" + cleaned;
    if (subs.find((s) => s.name.toLowerCase() === name.toLowerCase())) return;
    // Insert a bare row; the verification effect below resolves it against
    // /subreddit/info (DB-first), hydrates the metadata, and drops it if
    // Reddit can't resolve it — so a mistyped name never lingers on the radar.
    setSubs((arr) => [
      ...arr,
      {
        name,
        members: "",
        note: "",
        signal: "",
        communityIcon: "",
        on: true,
      },
    ]);
  };

  // Verify every not-yet-confirmed subreddit against Reddit (DB-first on the
  // server) and DROP any that don't resolve. The AI occasionally suggests
  // plausible-but-nonexistent community names, and a user can mistype an added
  // one — neither should sit on the radar, since we can't scan a sub that
  // doesn't exist. Rows that already carry an icon came from our cache/Reddit,
  // so they're known-real and skipped.
  React.useEffect(() => {
    const pending = subs.filter((s) => {
      const key = s.name.replace(/^r\//i, "").toLowerCase();
      return key && !s.verified && !s.communityIcon && !subredditIcons[key];
    });
    if (!pending.length) return;
    let cancelled = false;
    void (async () => {
      const meta = {};         // key -> { icon, members, signal, note }
      const invalid = new Set();
      await Promise.all(pending.map(async (s) => {
        const clean = s.name.replace(/^r\//i, "");
        const key = clean.toLowerCase();
        try {
          const resp = await window.api?.subreddit?.info?.(clean);
          const info = resp?.subreddit;
          if (!info) { invalid.add(key); return; }
          meta[key] = {
            icon: info.community_icon || "",
            members: formatSubscribers(info.subscribers),
            signal: inferSignal(info.subscribers),
            note: info.public_description || info.title || "",
          };
        } catch (e) {
          // 404 / refusal → Reddit can't resolve it → treat as not real.
          invalid.add(key);
        }
      }));
      if (cancelled) return;

      const foundIcons = {};
      for (const [k, v] of Object.entries(meta)) {
        if (v.icon) foundIcons[k] = v.icon;
      }
      if (Object.keys(foundIcons).length) {
        setSubredditIcons((prev) => ({ ...prev, ...foundIcons }));
      }

      if (!invalid.size && !Object.keys(meta).length) return;
      setSubs((arr) => arr
        .filter((s) => !invalid.has(s.name.replace(/^r\//i, "").toLowerCase()))
        .map((s) => {
          const key = s.name.replace(/^r\//i, "").toLowerCase();
          const v = meta[key];
          if (!v) return s;
          const hasMembers = s.members && s.members !== "—" && s.members !== "unknown";
          return {
            ...s,
            verified: true,
            communityIcon: s.communityIcon || v.icon,
            members: hasMembers ? s.members : (v.members || s.members),
            signal: s.signal || v.signal,
            note: s.note || v.note,
          };
        }));
    })();
    return () => {
      cancelled = true;
    };
  }, [subs, subredditIcons]);

  const activeCount = subs.filter((s) => s.on).length;
  const signalCount =
    jobs.length + pains.length + keywords.length + triggers.length + themes.length;

  const handleApprove = () => {
    if (submitting) return;
    const finalSubs = subs.filter((s) => s.on);
    const nextForm = {
      ...form,
      name: productName,
      website,
      description: pitch,
      customers: customer,
      useCases: jobs,            // jobs in the UI back the use_cases column
      painPoints: pains,
      keywords,
      avoid,
      subreddits: finalSubs.map((s) => s.name.replace(/^r\//i, "")),
    };
    const nextPlan = {
      target_customers: plan.target_customers || [customer].filter(Boolean),
      jobs_to_be_done: jobs,
      pain_points: pains,
      trigger_phrases: triggers,
      suggested_subreddits: finalSubs.map((s) => ({
        name: s.name.replace(/^r\//i, ""),
        size: s.members || "unknown",
        reason: s.note || "",
        signal: s.signal || "medium",
        community_icon: s.communityIcon || subredditIcons[s.name.replace(/^r\//i, "").toLowerCase()] || "",
      })),
      negative_matches: negatives,
      search_themes: themes,
    };
    onApprove?.(nextForm, nextPlan);
  };

  return (
    <div className="ob2-radar screen-enter">
      <div className="ob2-radar-head">
        <div className="ob2-eyebrow">
          <window.I.Check size={11} />
          Step 2 of 2 · Profile ready
        </div>
        <div className="ob2-radar-title-row">
          <h1 className="ob2-title">Here's your radar.</h1>
        
        </div>
        <p className="ob2-sub">
        You can adjust the signals to improve matching.
        </p>
        <div className="ob2-radar-head-actions">
            <button
              className="btn btn-ghost btn-sm ob2-radar-head-secondary"
              onClick={onRestart}
              aria-label="Start over"
              title="Start over"
              disabled={submitting}
            >
              <window.I.Refresh size={12} />
              <span className="ob2-radar-head-secondary-label">Start over</span>
            </button>
            <button
              className="btn btn-primary btn-sm ob2-radar-head-cta"
              onClick={handleApprove}
              disabled={submitting || activeCount === 0 || !productName.trim() || !pitch.trim()}
            >
              {submitting ? (
                <>
                  <window.I.Sparkles size={13} style={{ animation: "spin 900ms linear infinite" }} />
                  Creating business profile…
                </>
              ) : (
                <>
                  <window.I.Sparkles size={13} />
                  Finish setup
                </>
              )}
            </button>
          </div>
      </div>

      {/* Product card — inline editable */}
      <section className="ob2-product">
        {siteIcon ? (
          <img
            className="ob2-product-mark ob2-product-mark-image"
            src={siteIcon}
            alt=""
            onError={() => setSiteIconFailed(true)}
          />
        ) : (
          <div className="ob2-product-mark">
            {(productName || "—").slice(0, 2).toUpperCase()}
          </div>
        )}
        <div className="ob2-product-body">
          <div className="ob2-product-row">
            <input
              className="ob2-product-name"
              value={productName}
              onChange={(e) => setProductName(e.target.value)}
            />
            <span className="ob2-extract-tag">
              <window.I.Sparkles size={10} /> auto-extracted
            </span>
          </div>

          {editingPitch ? (
            <textarea
              className="ob2-product-pitch ob2-product-pitch-edit"
              value={pitch}
              onChange={(e) => setPitch(e.target.value)}
              onBlur={() => setEditingPitch(false)}
              autoFocus
              rows={2}
            />
          ) : (
            <div
              className="ob2-product-pitch"
              onClick={() => setEditingPitch(true)}
              title="Click to edit"
            >
              {pitch || <span style={{ color: "var(--text-tertiary)" }}>Add a one-line pitch…</span>}
              <button className="ob2-inline-edit" aria-label="Edit">
                <window.I.Edit size={11} />
              </button>
            </div>
          )}

          {website && (
            <div className="ob2-product-url">
              <window.I.External size={11} />
              {website}
            </div>
          )}
        </div>
      </section>

      {/* Customer + Pains — two cards side by side */}
      <div className="ob2-grid-2">
        <section className="ob2-mini-card">
          <div className="ob2-section-head">
            <div>
              <div className="ob2-section-title">
                <window.I.User size={13} />
                Who you're helping
              </div>
            </div>
          </div>
          {editingCustomer ? (
            <textarea
              className="ob2-customer-text ob2-customer-text-edit"
              value={customer}
              onChange={(e) => setCustomer(e.target.value)}
              onBlur={() => setEditingCustomer(false)}
              autoFocus
              rows={2}
            />
          ) : (
            <div
              className="ob2-customer-text"
              onClick={() => setEditingCustomer(true)}
              title="Click to edit"
            >
              {customer || <span style={{ color: "var(--text-tertiary)" }}>Describe who you're helping…</span>}
              <button className="ob2-inline-edit" aria-label="Edit">
                <window.I.Edit size={11} />
              </button>
            </div>
          )}
          <div className="ob2-sub-label">What your product does for them</div>
          <ChipEditor
            values={jobs}
            onChange={setJobs}
            placeholder="Add a job…"
          />
        </section>

        <section className="ob2-mini-card">
          <div className="ob2-section-head">
            <div>
              <div className="ob2-section-title">
                <window.I.Alert size={13} />
                Pain points
              </div>
              <div className="ob2-section-desc">What's the biggest pain your audience is facing?</div>
            </div>
          </div>
          <ChipEditor
            values={pains}
            onChange={setPains}
            placeholder="Add a pain point…"
          />
        </section>
      </div>

      {/* THE RADAR — the centerpiece */}
      <section className="ob2-radar-section">
        <div className="ob2-section-head ob2-radar-section-head">
          <div>
            <div className="ob2-section-title">
              <window.I.Target size={14} />
              Subreddit radar
            </div>
            <div className="ob2-section-desc">
              <strong>{activeCount}</strong> {activeCount === 1 ? "community" : "communities"}
            </div>
          </div>
          {!addingSub && (
            <button className="ob2-add-btn" onClick={() => setAddingSub(true)}>
              <window.I.Plus size={12} />
              Add subreddit
            </button>
          )}
        </div>

        <div className="ob2-sublist">
          {subs.map((s) => {
            const hasSignal = s.signal === "high" || s.signal === "medium" || s.signal === "low";
            const hasMembers = s.members && s.members !== "—" && s.members !== "unknown";
            return (
            <div
              key={s.name}
              className={"ob2-subrow " + (s.on ? "" : "is-off")}
            >
              <button
                className="ob2-sub-toggle"
                onClick={() => toggleSub(s.name)}
                aria-label={s.on ? "Disable" : "Enable"}
                title={s.on ? "Disable this subreddit" : "Enable this subreddit"}
              >
                <span className="ob2-sub-toggle-track">
                  <span className="ob2-sub-toggle-knob" />
                </span>
              </button>

              {hasSignal ? (
                <div className="ob2-sub-signal" title={`Signal: ${s.signal}`}>
                  <SignalBars level={s.signal} />
                </div>
              ) : (
                <div className="ob2-sub-signal" aria-hidden="true" />
              )}

              <SubredditAvatar
                name={s.name}
                icon={s.communityIcon || subredditIcons[s.name.replace(/^r\//i, "").toLowerCase()] || ""}
              />

              <div className="ob2-sub-main">
                <div className="ob2-sub-name-row">
                  <span className="ob2-sub-name">{s.name}</span>
                  {hasMembers && (
                    <span className="ob2-sub-members">{s.members} members</span>
                  )}
                  {hasSignal && (
                    <span className={"ob2-sub-tier ob2-sub-tier-" + s.signal}>
                      {s.signal === "high" ? "Strong signal" : s.signal === "medium" ? "Moderate" : "Adjacent"}
                    </span>
                  )}
                </div>
                {s.note && <div className="ob2-sub-note">{s.note}</div>}
              </div>

              <button
                className="ob2-sub-remove"
                onClick={() => removeSub(s.name)}
                title="Remove from radar"
                aria-label="Remove"
              >
                <window.I.X size={14} />
              </button>
            </div>
            );
          })}

          {subs.length === 0 && !addingSub && (
            <div className="ob2-sublist-empty">
              <window.I.Layers size={14} />
              No subreddits mapped yet. Add at least one so we have a place to scan.
            </div>
          )}

          {addingSub && (
            <AddSubRow
              onAdd={(v) => { addSub(v); setAddingSub(false); }}
              onCancel={() => setAddingSub(false)}
            />
          )}
        </div>
      </section>

      {/* Match signals header */}
      <div className="ob2-signals-meta">
        <div className="ob2-signals-meta-title">
          <window.I.Sparkles size={13} />
          Match signals
        </div>
        <div className="ob2-signals-meta-desc">
          <strong>{signalCount}</strong> signals teaching our scorer what fits.
          These get matched against every post inside the radar.
        </div>
      </div>

      {/* Topics — two compact chip editors */}
      <div className="ob2-tune">
        <div className="ob2-tune-card ob2-tune-positive">
          <div className="ob2-tune-head">
            <span className="ob2-tune-dot ob2-tune-dot--pos" />
            Listen for
            <span className="ob2-tune-count">{keywords.length + triggers.length}</span>
          </div>
          <div className="ob2-tune-desc">
            Words and exact phrases that boost a post's match score.
          </div>

          <div className="ob2-sub-label">Topics</div>
          <ChipEditor values={keywords} onChange={setKeywords} accent placeholder="Add a topic…" />

          <div className="ob2-sub-label">Trigger phrases <span className="ob2-sub-label-hint">exact match</span></div>
          <ChipEditor values={triggers} onChange={setTriggers} accent mono placeholder='Add a "phrase"…' />
        </div>

        <div className="ob2-tune-card ob2-tune-negative">
          <div className="ob2-tune-head">
            <span className="ob2-tune-dot ob2-tune-dot--neg" />
            Skip
            <span className="ob2-tune-count">{avoid.length + negatives.length}</span>
          </div>
          <div className="ob2-tune-desc">
            Patterns that disqualify a post even when keywords match.
          </div>

          <div className="ob2-sub-label">Words to ignore</div>
          <ChipEditor values={avoid} onChange={setAvoid} placeholder="Add a word…" />

          <div className="ob2-sub-label">Negative patterns <span className="ob2-sub-label-hint">conversation-level</span></div>
          <ListEditor
            values={negatives}
            onChange={setNegatives}
            placeholder="Describe a kind of conversation to skip…"
          />
        </div>
      </div>

      {/* Themes — collapsible, broader topical clusters */}
      <section className="ob2-themes">
        <button
          className="ob2-themes-toggle"
          onClick={() => setAdvancedOpen((v) => !v)}
          aria-expanded={advancedOpen}
        >
          <window.I.Layers size={13} />
          <span>
            Themes we'll track
            <span className="ob2-themes-count">{themes.length}</span>
          </span>
          <span className="ob2-themes-desc">
            Broader topical clusters used to widen the search beyond keywords.
          </span>
          <window.I.Chevron
            size={14}
            style={{
              transform: advancedOpen ? "rotate(180deg)" : "none",
              transition: "transform 180ms var(--ease)",
              color: "var(--text-tertiary)",
              marginLeft: "auto",
            }}
          />
        </button>
        {advancedOpen && (
          <div className="ob2-themes-body">
            <ChipEditor values={themes} onChange={setThemes} placeholder="Add a theme…" />
          </div>
        )}
      </section>

      {/* Foot */}
      <div className="ob2-foot">
        <div className="ob2-foot-trust">
          <window.I.ShieldCheck size={13} />
          You can fine-tune this radar from your profile any time after setup.
        </div>
        <div className="spacer" />
        <div className="ob2-foot-actions">
          <button
            className="btn btn-primary btn-sm ob2-foot-btn-primary"
            onClick={handleApprove}
            disabled={submitting || activeCount === 0 || !productName.trim() || !pitch.trim()}
          >
            {submitting ? (
              <>
                <window.I.Sparkles size={13} style={{ animation: "spin 900ms linear infinite" }} />
                Creating business profile…
              </>
            ) : (
              <>
                <window.I.Sparkles size={13} />
                Finish setup
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}


/* ─────────── Small pieces ─────────── */

function mergeSubreddits(formSubs, planSubs) {
  /* Combine the form's raw subreddit list with the search plan's richer
     suggested_subreddits (which carry size/reason/signal). The plan wins
     on metadata. Names are normalized to "r/<name>" without duplicates. */
  const byName = new Map();
  for (const raw of formSubs || []) {
    const clean = String(raw || "").trim().replace(/^r\//i, "").replace(/^\/+/, "");
    if (!clean) continue;
    const key = clean.toLowerCase();
    byName.set(key, {
      name: "r/" + clean,
      members: "—",
      note: "From your inputs · we'll learn its volume on first scan",
      signal: "medium",
      communityIcon: "",
      on: true,
    });
  }
  for (const s of planSubs || []) {
    if (!s || !s.name) continue;
    const clean = String(s.name).trim().replace(/^r\//i, "").replace(/^\/+/, "");
    if (!clean) continue;
    const key = clean.toLowerCase();
    byName.set(key, {
      name: "r/" + clean,
      members: s.size || "unknown",
      note: s.reason || "",
      signal: s.signal || "medium",
      communityIcon: s.community_icon || "",
      on: true,
    });
  }
  return Array.from(byName.values());
}

function SignalBars({ level }) {
  const n = level === "high" ? 3 : level === "medium" ? 2 : 1;
  return (
    <span className={"ob2-bars ob2-bars-" + level} aria-hidden="true">
      <span className={"ob2-bar " + (n >= 1 ? "is-on" : "")} />
      <span className={"ob2-bar " + (n >= 2 ? "is-on" : "")} />
      <span className={"ob2-bar " + (n >= 3 ? "is-on" : "")} />
    </span>
  );
}

function AddSubRow({ onAdd, onCancel }) {
  const [v, setV] = React.useState("");
  const ref = React.useRef(null);
  React.useEffect(() => { ref.current?.focus(); }, []);
  return (
    <div className="ob2-subrow ob2-subrow-add">
      <span className="ob2-add-leading">r/</span>
      <input
        ref={ref}
        className="ob2-add-input"
        placeholder="name or reddit.com/r/… URL"
        value={v}
        onChange={(e) => setV(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") onAdd(v);
          if (e.key === "Escape") onCancel();
        }}
      />
      <button className="btn btn-secondary btn-sm" onClick={onCancel}>Cancel</button>
      <button className="btn btn-primary btn-sm" onClick={() => onAdd(v)} disabled={!v.trim()}>
        Add
      </button>
    </div>
  );
}

function ChipEditor({ values, onChange, accent = false, mono = false, placeholder = "Add…" }) {
  const [draft, setDraft] = React.useState("");
  const ref = React.useRef(null);
  const add = (raw) => {
    const t = (raw || "").trim();
    if (!t || values.includes(t)) { setDraft(""); return; }
    onChange([...values, t]);
    setDraft("");
  };
  return (
    <div className="ob2-chips" onClick={() => ref.current?.focus()}>
      {values.map((v) => (
        <span
          key={v}
          className={"ob2-chip " + (accent ? "is-accent " : "") + (mono ? "is-mono " : "")}
        >
          {v}
          <button
            className="ob2-chip-x"
            onClick={(e) => { e.stopPropagation(); onChange(values.filter((x) => x !== v)); }}
            aria-label="Remove"
          >
            <window.I.X size={10} />
          </button>
        </span>
      ))}
      <input
        ref={ref}
        className={"ob2-chip-input " + (mono ? "is-mono" : "")}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(draft); }
          if (e.key === "Backspace" && !draft && values.length) {
            onChange(values.slice(0, -1));
          }
        }}
        onBlur={() => draft && add(draft)}
        placeholder={values.length ? "" : placeholder}
      />
    </div>
  );
}

function ListEditor({ values, onChange, placeholder = "Describe a kind of conversation to skip…" }) {
  const [draft, setDraft] = React.useState("");
  const add = () => {
    const t = draft.trim();
    if (!t || values.includes(t)) { setDraft(""); return; }
    onChange([...values, t]);
    setDraft("");
  };
  return (
    <div className="ob2-listed">
      {values.map((v, i) => (
        <div key={i} className="ob2-listed-row">
          <span className="ob2-listed-bullet" />
          <span className="ob2-listed-text">{v}</span>
          <button
            className="ob2-listed-x"
            onClick={() => onChange(values.filter((_, j) => j !== i))}
            aria-label="Remove"
          >
            <window.I.X size={11} />
          </button>
        </div>
      ))}
      <div className="ob2-listed-add">
        <input
          className="ob2-listed-input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          onBlur={() => draft && add()}
          placeholder={placeholder}
        />
      </div>
    </div>
  );
}


window.OnboardingShell = OnboardingShell;
window.DescribeProductScreen = DescribeProductScreen;
window.RadarReviewScreen = RadarReviewScreen;
window.AnalyzingTransition = AnalyzingTransition;
window.SignalBars = SignalBars;
